import { RouterProvider, createRoute, createRouter, redirect } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";

import { rootRoute } from "./baseRoutes";
import { webappRoute } from "./webapp/Routes";
import Loading, { AppLoading } from "./webapp/components/Loading";
import { AnalyticsProvider } from "./context/o11y";
import { ErrorBoundary, ErrorPreview } from "./webapp/components/ErrorBoundary";
import { AppQueryProvider } from "./context/query";
import { HasuraProvider } from "./context/hasura/provider";
import { QuestApiProvider } from "./context/quest";
import { questDbUrl } from "./globals";
import { RefreshAccessToken } from "./context/auth/Refresh";
import { BlockedUserGuard } from "./context/auth/BlockedUserGuard";
import { AuthProvider } from "./context/auth/provider";
import { FeatureFlagsProvider } from "./context/flags";
import { DailyReloadProvider } from "./context/reload";
import { ApiConsole } from "./shell/ApiConsole";
import { ProductsPublisher } from "./shell/ProductsPublisher";
import { ArtisAppProvider } from "./shell/ArtisAppProvider";

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({
      to: "/app/market",
    });
  },
});

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/*",
  beforeLoad: () => {
    throw redirect({
      to: "/app/market",
    });
  },
});

const routeTree = rootRoute.addChildren([indexRoute, webappRoute, catchAllRoute]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <Loading showLogo syncing={false} />,
  defaultErrorComponent: (e) => {
    console.error(e);
    return (
      <div className="w-full h-dvh">
        <ErrorPreview error={e.error} componentStack={e.info?.componentStack} resetError={e.reset} />
      </div>
    );
  },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export const App = Sentry.withProfiler(() => {
  return (
    <ErrorBoundary>
      <DailyReloadProvider>
        <AppQueryProvider>
          <AuthProvider>
            <AppLoading />
            <QuestApiProvider baseUrl={new URL(questDbUrl()).origin}>
              <HasuraProvider>
                <RefreshAccessToken />
                <BlockedUserGuard>
                  <FeatureFlagsProvider>
                    <ArtisAppProvider>
                      <AnalyticsProvider>
                        <ProductsPublisher />
                        <ApiConsole />
                        <RouterProvider router={router} />
                      </AnalyticsProvider>
                    </ArtisAppProvider>
                  </FeatureFlagsProvider>
                </BlockedUserGuard>
              </HasuraProvider>
            </QuestApiProvider>
          </AuthProvider>
        </AppQueryProvider>
      </DailyReloadProvider>
    </ErrorBoundary>
  );
});
